<template>
  <button
    :title="title"
    :class="{
      default: !className && !svg && !imageSrc && !variant,
      'image-button': !!imageSrc,
      transparent: transparent,
      svg: !!svg,
      'box-shadow-transition-green': !isDisabled && variant === 'plus',
      'box-shadow-transition-red': !isDisabled && variant === 'minus',
      [`${className}`]: !!(className && className.length),
      [`${variant}`]: !!variant,
      active: isActive,
    }"
    :disabled="isDisabled"
    @click="handleClick ? handleClick() : undefined"
    :style="{
      width: width ? width : fullWidth ? '100%' : undefined,
      height: height ? height : fullHeight ? '100%' : undefined,
      fontSize,
    }"
  >
    <slot v-if="!text" />
    <div
      v-if="svg"
      class="svg-wrapper"
    >
      <a
        v-if="link"
        :href="link"
        target="_blank"
        rel="noopener noreferrer"
      >
        <svg
          :style="{
            width: width,
            height: height,
          }"
          :viewBox="svgData[svg].viewBox"
        >
          <path :d="svgData[svg].path" />
        </svg>
      </a>
      <svg
        v-if="!link"
        :viewBox="svgData[svg].viewBox"
        :style="{ width: svgWidth, height: svgHeight, fill: svgColor }"
      >
        <path :d="svgData[svg].path" />
      </svg>
    </div>
    <div
      v-if="imageSrc"
      class="image-wrapper"
    >
      <img
        class="image"
        :class="{
          'image-active': isActive,
          'image-inactive': !isActive,
          'image-disabled': isDisabled,
        }"
        :style="{
          width: imageWidth,
          height: imageHeight,
        }"
        :src="imageSrc"
        alt="button image"
      >
    </div>
    <span
      v-if="activeLevel >= 0"
      class="overlay-active-level"
    >{{
      activeLevel
    }}</span>
    {{ text }}
  </button>
</template>
<script>
import { defineComponent } from "vue";
import { useStore } from "vuex";
import svgData from "@/data/svg";

export default defineComponent({
  name: "GenericButton",
  props: {
    handleClick: {
      type: Function,
      default: undefined,
    },
    text: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    variant: {
      type: String,
      default: "",
    },
    className: {
      type: String,
      default: "",
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: undefined,
    },
    height: {
      type: String,
      default: undefined,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    fullHeight: {
      type: Boolean,
      default: false,
    },
    fontSize: {
      type: String,
      default: undefined,
    },
    transparent: {
      type: Boolean,
      default: false,
    },
    svg: {
      type: String,
      default: "",
    },
    svgWidth: {
      type: String,
      default: "",
    },
    svgHeight: {
      type: String,
      default: "",
    },
    svgColor: {
      type: String,
      default: "",
    },
    link: {
      type: String,
      default: "",
    },
    img: {
      type: Boolean,
      default: false,
    },
    imageSrc: {
      type: String,
      default: "",
    },
    imageWidth: {
      type: String,
      default: "100%",
    },
    imageHeight: {
      type: String,
      default: "",
    },
    activeLevel: {
      type: Number,
      default: undefined,
    },
  },
  setup() {
    const store = useStore();
    return {
      state: store.state,
      build: store.state.build,
      svgData,
    };
  },
});
</script>
<style scoped>
.overlay-active-level {
  position: absolute;
  bottom: -2px;
  right: 4px;
  font-family: var(--font-family-gothic);
  font-size: 16px;
  color: var(--text-white);
  -webkit-text-stroke: var(--text-stroke-black);
  text-shadow: 2px 1px 5px black;
}
.default {
  align-items: center;
  appearance: none;
  background-color: rgba(133, 252, 90, 0.4);
  /* background-color: rgba(133, 252, 90, 0); */
  border-radius: 4px;
  border-width: 0;
  border: 1px solid #3d3d44;
  box-shadow: rgba(45, 35, 66, 0.4) 2px 2px 0px;
  box-sizing: border-box;
  color: white;
  cursor: pointer;
  display: inline-flex;
  /* font-family: "JetBrains Mono",monospace; */
  letter-spacing: 1px;
  justify-content: center;
  list-style: none;
  overflow: hidden;
  padding: 2px 12px 2px;
  position: relative;
  text-align: center;
  text-decoration: none;
  transition: box-shadow 0.15s, transform 0.15s, background-color 1s;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  white-space: wrap;
  will-change: box-shadow, transform;
  /* margin: auto; */
  font-size: 20px;
  font-family: var(--font-family-gothic);
  text-shadow: var(--text-shadow);
  -webkit-text-stroke: var(--text-stroke-black);
  -webkit-text-stroke: 1px #0000006e;
}
.default:hover {
  box-shadow: rgba(45, 35, 66, 0.4) 2px 2px 0px,
    rgba(45, 35, 66, 0.3) 3px 3px 6px -1px,
    var(--text-tagged) -1px -1px 6px inset;
  background-color: rgba(133, 252, 90, 0.7);
}
.default:active {
  box-shadow: #3d3d44 1px 1px 1px inset, #3d3d44 1px 1px 0px inset,
    var(--text-tagged) -1px -1px 12px inset;
  transform: translateY(1px) translateX(1px);
}
.default[disabled],
.default[disabled]:hover,
.default[disabled]:focus,
.default[disabled]:active {
  box-shadow: #3b3b3f 0 -3px 25px inset;
  background-color: var(--text-disabled);
  cursor: not-allowed;
}

.image-wrapper {
  display: flex;
  justify-content: space-evenly;
  width: 100%;
  height: 100%;
}

.image-button {
  align-items: center;
  appearance: none;
  border-radius: 4px;
  border-width: 0;
  border: 1px solid transparent;
  box-sizing: border-box;
  background-color: transparent;
  color: white;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  justify-content: space-between;
  position: relative;
  transition: box-shadow 0.15s, transform 0.15s, background-color 1s;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  white-space: wrap;
  will-change: box-shadow, transform;
  padding: 3px;
}
.image-button:hover:not(.active) {
  box-shadow: rgba(45, 35, 66, 0.4) 2px 2px 0px,
    rgba(45, 35, 66, 0.3) 3px 3px 6px -1px,
    var(--text-tagged) -1px -1px 6px inset;
  background-color: rgba(133, 252, 90, 0.233);
  border: 1px solid #cccccc;
}
.image-button:active {
  box-shadow: #3d3d44 1px 1px 1px inset, #3d3d44 1px 1px 0px inset,
    var(--text-tagged) -1px -1px 12px inset;
  transform: translateY(1px) translateX(1px);
}
.image-button[disabled],
.image-button[disabled]:focus,
.image-button[disabled]:active {
  /* box-shadow: #3b3b3f 0 -3px 25px inset; */
  /* background-color: var(--text-disabled); */
  cursor: not-allowed;
}
.image-button[disabled]:hover:not(.active) {
  background-color: transparent;
  box-shadow: none;
  border: 1px solid transparent;
}

.image {
  -webkit-filter: grayscale(70%) drop-shadow(2px 2px 2px #222);
  filter: grayscale(70%) drop-shadow(2px 2px 2px #222);
}
.image:hover {
  -webkit-filter: contrast(120%) brightness(1.5) drop-shadow(1px 1px 1px #222);
  filter: contrast(120%) brightness(1.5) drop-shadow(1px 1px 1px #222);
}
.active {
  -webkit-filter: contrast(120%) brightness(1.5) drop-shadow(1px 1px 1px #222);
  filter: contrast(120%) brightness(1.5) drop-shadow(1px 1px 1px #222);
  /* margin-left: 6px; */
  border-radius: 4px;
  border: var(--border-light);
  background-color: #5e795767;
  /* box-shadow: #85fc5ab3 0 0px 20px inset; */
  /* background-color: #85fc5ab3; */
  /* box-shadow: #3d3d44 1px 1px 1px inset, #3d3d44 1px 1px 0px inset, var(--text-tagged) -1px -1px 12px inset; */
  /* transform: translateY(1px) translateX(1px); */
}
.active:hover {
  background-color: #75da5c7a;
}

.image-active {
  -webkit-filter: contrast(100%) brightness(1) drop-shadow(1px 1px 1px #222);
  filter: contrast(100%) brightness(1) drop-shadow(1px 1px 1px #222);
}
.image-active:hover {
  /* background-color: #abc4a567; */
  /* -webkit-filter: contrast(120%) brightness(1.5) drop-shadow(1px 1px 1px #222);
  filter: contrast(120%) brightness(1.5) drop-shadow(1px 1px 1px #222); */
  box-shadow: none;
  /* cursor: default; */
}
.image-disabled:not(.image-active) {
  cursor: not-allowed;
}

::v-slotted(.a) {
  color: white;
  font-size: 14px;
  font-family: var(--font-family-gothic);
  text-shadow: var(--text-shadow);
  -webkit-text-stroke: var(--text-stroke-black);
  -webkit-text-stroke: 1px #0000006e;
  text-decoration: none;
}
::v-slotted(.a:hover) {
  color: cornsilk;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.7);
}
::v-slotted(.img) {
  width: 100%;
  height: 100%;
  cursor: pointer;
}
/* MODIFIERS */
.minus,
.plus {
  cursor: pointer;
  margin-top: auto;
  margin-bottom: auto;
  background: transparent;
  padding: 0px;
  line-height: 1px;
  font-family: var(--font-family-gothic);
  font-size: 22px;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.4);
  border: var(--border-light);
  border-radius: 4px;
}
.minus {
  color: #de9d9dc6;
}
.plus {
  color: rgba(107, 179, 111, 0.775);
}
.minus:enabled:hover {
  color: rgb(255, 144, 144);
}
.plus:enabled:hover {
  color: rgb(103, 235, 110);
}
.plus:enabled:active,
.minus:enabled:active {
  transform: scale(0.95);
}

.plus[disabled],
.minus[disabled] {
  cursor: not-allowed;
  color: #666666;
}

.xs {
  padding: 0px 8px;
}
.transparent {
  background-color: transparent;
}
svg {
  width: 20px;
  /* height: 24px; */
  fill: var(--text-green);
}
.svg-wrapper {
  display: flex;
}
.svg {
  align-items: center;
  appearance: none;
  /* background-color: rgba(133, 252, 90, 0.4); */
  background-color: transparent;
  /* border-radius: 5px; */
  border-width: 0;
  /* border: 1px solid #3d3d44; */
  /* box-shadow: rgba(45, 35, 66, 0.4) 2px 2px 0px; */
  box-sizing: border-box;
  color: white;
  cursor: pointer;
  display: inline-flex;
  /* font-family: "JetBrains Mono",monospace; */
  /* letter-spacing: 1px; */
  justify-content: center;
  list-style: none;
  overflow: hidden;
  /* padding: 2px 12px 2px; */
  position: relative;
  text-align: center;
  text-decoration: none;
  transition: box-shadow 0.15s, transform 0.15s, fill 1s;
  touch-action: manipulation;
  white-space: wrap;
  will-change: box-shadow, transform;
  -webkit-filter: drop-shadow(3px 3px 2px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(3px 3px 2px rgba(0, 0, 0, 0.7));
}
svg:hover {
  fill: var(--text-yellow);
  /* box-shadow: var(--text-yellow) 0px 0px 2px; */
}
svg:active {
  transform: translateY(1px) translateX(1px);
  fill: white;
}
svg[disabled],
svg[disabled]:hover,
svg[disabled]:focus,
svg[disabled]:active {
  fill: var(--text-disabled);
  cursor: not-allowed;
}

/* VARIANT TEXT */
.text {
  font-size: 14px;
  border: none;
  box-shadow: none;
  background: none;
  color: inherit;
  font-family: var(--font-family-fixedsys);
  text-shadow: var(--text-shadow);
  padding-inline: 0px;
}
</style>
